'use client';

import { Box, Typography } from '@coolblue-development/freddy';
import viewEntrancesTrigger from '@coolblue-development/ga4-events/triggers/AccessoryEntrances/viewEntrancesTrigger';
import { TrackWhenVisible } from '@coolblue-development/next-analytics/client';
import { AccessoryEntrance } from '@features/accessory-categories/types';
import useIntersectionObserver from '@hooks/useIntersectionObserver';
import { useTranslation } from '@i18n/useTranslation';
import { useEffect, useState } from 'react';

import AccessoryEntrancesBase from './AccessoryEntrancesBase';
import AccessoryEntrancesSkeleton from './AccessoryEntrancesSkeleton';
import { fetchAccessoryEntrances } from './actions/fetchAccessoryEntrances';
import { getAccessoryCategoryProductIds } from './utils/getAccessoryCategoryProductIds';
import ViewAllAccessoriesLink from './ViewAllAccessoriesLink';

type Props = {
  productId: number;
  accessoryCategoriesUrl?: string;
  hasAccessories?: boolean;
};

export default function AccessoryEntrances({
  productId,
  accessoryCategoriesUrl,
  hasAccessories
}: Props) {
  const { t } = useTranslation('webshop');
  const { targetRef, isIntersecting } = useIntersectionObserver();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AccessoryEntrance[] | null>(null);

  useEffect(() => {
    if (!isIntersecting || data) return;

    fetchAccessoryEntrances({
      productId,
      limit: 4
    })
      .then(fetchedData => {
        setData(fetchedData);
      })
      .finally(() => setLoading(false));
  }, [data, isIntersecting, productId]);

  if (!hasAccessories) {
    return null;
  }

  return (
    <Box ref={targetRef} component='section' id='accessory-entrances'>
      {loading && !data && <AccessoryEntrancesSkeleton />}

      {!loading && data && data.length > 0 && (
        <>
          <Typography variant='h2' mb={400}>
            {t('productPage.suitableAccessories')}
          </Typography>

          <TrackWhenVisible
            trigger={viewEntrancesTrigger({
              item_ids: getAccessoryCategoryProductIds(data).join(',')
            })}
          >
            <AccessoryEntrancesBase accessoryCategories={data} />

            {data.length >= 4 && (
              <ViewAllAccessoriesLink href={accessoryCategoriesUrl || ''}>
                {t('productPage.allAccessories')}
              </ViewAllAccessoriesLink>
            )}
          </TrackWhenVisible>
        </>
      )}
    </Box>
  );
}
